import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementTypesById } from "core-integration/src/store/element-types/reducers";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
} from "core-integration/src/store/elements/thunks";
import {
  IElement,
  IElementControls,
} from "core-integration/src/store/elements/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import { ellipsis } from "polished";
import React, { useEffect, useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import styled from "styled-components";
import { getStatDetails } from "../../utils/stats";
import Alert from "../Alert";
import Box from "../Box";
import ElementFilter from "../element-controls/ElementFilter";
import ElementSort from "../element-controls/ElementSort";
import Paginator, { paginate } from "../element-controls/Paginator";
import ElementDialogButton from "../ElementDialogButton";
import ElementShirt from "../ElementShirt";
import { ElementRow, ElementTable } from "../ElementTable";
import { FieldCol, FieldCols } from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Media from "../Media";
import Panel from "../Panel";
import { ScrollTable } from "../Table";
import Title from "../Title";
import Tooltip, { TooltipLabel } from "../Tooltip";

const StatsStatus = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 7%;
  }
`;

const StatsElement = styled.th`
  width: 40%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 38%;
  }
`;

const StatsCost = styled.th`
  width: 10%;
  font-size: ${({ theme }) => theme.fontSizes[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsSel = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsForm = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsPts = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsExtra = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const ElementNameButton = styled.button`
  ${ellipsis()};
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: inherit;
  font-family: ${({ theme }) => theme.fonts.base};
  text-align: start;
  cursor: pointer;
`;

const Name = styled.div`
  ${ellipsis()};
  font-size: ${({ theme }) => theme.fontSizes[2]};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`;

const Info = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[2]};
`;

const Team = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: medium;
`;

const Position = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

type Props = WithTranslation;

const Statistics: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { statName } = useParams();
  const [page, setPage] = useState<number>(1);

  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elements = useSelector(getElementsFromControls);
  const elementTypesById = useSelector((state: RootState) =>
    getElementTypesById(state),
  );
  const teamsById = useSelector(getTeamsById);

  const updateControls = (controls: IElementControls) =>
    dispatch(updateElementControls(controls));

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      filter: e.target.value,
    });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    updateControls({
      ...controls,
      sort: e.target.value,
    });
  };

  const defaultStats: Array<keyof IElement> = [
    "now_cost",
    "selected_by_percent",
    "form",
    "total_points",
  ];

  useEffect(() => {
    dispatch(
      updateElementControlsAndMaxCost({
        ...controls,
        filter: "all",
        sort: statName || "total_points",
        search: "",
      }),
    );
  }, []);

  const { data, totalPages } = paginate(elements.data, page, 30);
  const extraStat =
    defaultStats.indexOf(controls.sort) === -1 ? controls.sort : "";
  const statDetail = getStatDetails(String(controls.sort), t);

  return (
    <Wrapper>
      <Main>
        <Box mx={2} mb={4}>
          <Title>{t("statistics.title", "Statistics")}</Title>
        </Box>
        <form>
          <FieldCols>
            <FieldCol>
              <ElementFilter handleFilterChange={handleFilterChange} />
            </FieldCol>
            <FieldCol>
              <ElementSort handleSortChange={handleSortChange} />
            </FieldCol>
          </FieldCols>
        </form>
        <Box mb={4}>
          <Alert type="info" $textAlign="start">
            {statDetail ? statDetail.description : ""}
          </Alert>
        </Box>
        <Panel>
          <ScrollTable>
            <ElementTable>
              <thead>
                <tr>
                  <StatsStatus>&nbsp;</StatsStatus>
                  <StatsElement>
                    {t("statistics.player", "Player")}
                  </StatsElement>
                  <StatsCost>{t("statistics.currency", "€")}</StatsCost>
                  <StatsSel>
                    <Tooltip
                      content={t("statistics.selectedByLong", "Selected by %")}
                    >
                      <TooltipLabel>
                        {t("statistics.selectedByShort", "Sel.")}
                      </TooltipLabel>
                    </Tooltip>
                  </StatsSel>
                  <StatsForm>{t("statistics.form", "Form")}</StatsForm>
                  <StatsPts>
                    <Tooltip
                      content={t("statistics.totalPoints", "Total points")}
                    >
                      <TooltipLabel>
                        {t("statistics.totalPointsShort", "TP.")}
                      </TooltipLabel>
                    </Tooltip>
                  </StatsPts>
                  {extraStat ? <StatsExtra>**</StatsExtra> : null}
                </tr>
              </thead>
              <tbody>
                {data.map((e) => (
                  <ElementRow key={e.id}>
                    <td>
                      <ElementDialogButton elementId={e.id} variant="list" />
                    </td>
                    <td>
                      <ElementNameButton
                        onClick={() => dispatch(showElementSummary(e.id))}
                      >
                        <Media
                          img={<ElementShirt elementId={e.id} sizes="24px" />}
                          centred={true}
                        >
                          <Name>{e.web_name}</Name>
                          <Info>
                            <Team>{teamsById[e.team].short_name}</Team>
                            <Position>
                              {
                                elementTypesById[e.element_type]
                                  .singular_name_short
                              }
                            </Position>
                          </Info>
                        </Media>
                      </ElementNameButton>
                    </td>
                    <td>{integerToMoney(e.now_cost, currencyDivisor)}</td>
                    <td>{e.selected_by_percent}%</td>
                    <td>{e.form}</td>
                    <td>{e.total_points}</td>
                    {extraStat ? <td>{e[extraStat]}</td> : null}
                  </ElementRow>
                ))}
              </tbody>
            </ElementTable>
          </ScrollTable>
        </Panel>
        <Box mt={4} mb={6}>
          <Paginator totalPages={totalPages} page={page} setPage={setPage} />
        </Box>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(Statistics);
